'use client';
import React from 'react';
import { Lightbulb, Dna, Palette, Package, Share2, Rocket } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

export default function HowItWorksSection() {
  const { t } = useLanguage();
  const hw = t?.howItWorks;
  const db = t?.dreamBuilder;

  const STEPS = [
    {
      icon: Lightbulb,
      emoji: '💡',
      label: db?.steps?.dream ?? 'Dream',
      color: 'text-primary',
      bg: 'bg-primary/10 border-primary/20',
      glow: 'shadow-primary/20',
      description: hw?.dreamDesc ?? 'Describe your idea in plain language. No business plan needed — just your vision.',
    },
    {
      icon: Dna,
      emoji: '🧬',
      label: db?.steps?.dna ?? 'Dream DNA™',
      color: 'text-accent',
      bg: 'bg-accent/10 border-accent/20',
      glow: 'shadow-accent/20',
      description: hw?.dnaDesc ?? 'Dream DNA Studio™ analyzes the full DNA of your dream — strengths, opportunities, risks, and business model.',
    },
    {
      icon: Palette,
      emoji: '🎨',
      label: db?.steps?.brand ?? 'Brand',
      color: 'text-yellow-400',
      bg: 'bg-yellow-400/10 border-yellow-400/20',
      glow: 'shadow-yellow-400/20',
      description: hw?.brandDesc ?? 'Your brand kit is built — name, colors, slogans, logo concepts, and brand personality.',
    },
    {
      icon: Package,
      emoji: '📦',
      label: db?.steps?.products ?? 'Products',
      color: 'text-mint',
      bg: 'bg-mint/10 border-mint/20',
      glow: 'shadow-mint/20',
      description: hw?.productsDesc ?? 'Product strategy, pricing, packaging, and revenue model are mapped out with clarity.',
    },
    {
      icon: Share2,
      emoji: '📣',
      label: db?.steps?.content ?? 'Content',
      color: 'text-pink-400',
      bg: 'bg-pink-400/10 border-pink-400/20',
      glow: 'shadow-pink-400/20',
      description: hw?.contentDesc ?? 'Content plan, social media strategy, captions, and a 7-day calendar are created for you.',
    },
    {
      icon: Rocket,
      emoji: '🚀',
      label: db?.steps?.launch ?? 'Launch',
      color: 'text-green-400',
      bg: 'bg-green-400/10 border-green-400/20',
      glow: 'shadow-green-400/20',
      description: hw?.launchDesc ?? 'Execute your 90-day roadmap with confidence. Track progress and celebrate every milestone.',
    },
  ];

  return (
    <section className="py-20 bg-background relative overflow-hidden">
      <div className="absolute inset-0 card-grid-bg opacity-30 pointer-events-none" />
      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-10 xl:px-14">
        <div className="text-center mb-14">
          <p className="text-xs font-700 uppercase tracking-widest text-primary mb-3">
            {hw?.sectionLabel ?? 'The Dream DNA™ Process'}
          </p>
          <h2 className="text-3xl md:text-4xl font-800 text-foreground mb-4 leading-tight">
            {hw?.sectionTitle ?? 'How Dream DNA Studio™'} <span className="gradient-text">{hw?.sectionTitleHighlight ?? 'Works'}</span>
          </h2>
          <p className="text-base text-muted-foreground max-w-xl mx-auto leading-relaxed">
            {hw?.sectionSubtitle ?? 'Six steps from a spark of inspiration to a fully launched project — powered by Dream DNA Studio™ every step of the way.'}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 items-start justify-center">
          {STEPS?.map((step, i) => {
            return (
              <div key={step?.label} className="flex flex-col items-center text-center">
                <div
                  className={`w-16 h-16 rounded-2xl border ${step?.bg} flex items-center justify-center mb-4 shadow-lg ${step?.glow} float-anim`}
                  style={{ animationDelay: `${i * 0.25}s` }}
                >
                  <span className="text-2xl">{step?.emoji}</span>
                </div>
                <span className={`text-[10px] font-700 uppercase tracking-widest ${step?.color} mb-1`}>
                  {hw?.stepLabel ?? 'Step'} {i + 1}
                </span>
                <h3 className="text-sm font-800 text-foreground mb-2">{step?.label}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed px-1">
                  {step?.description}
                </p>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-14">
          <p className="text-sm text-muted-foreground">
            {hw?.readyText ?? 'Ready to begin?'}{' '}
            <a href="/#dream-input" className="text-primary font-600 hover:underline">
              {hw?.readyCta ?? 'Discover your Dream DNA™ ↑'}
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
